// client/src/pages/Accounts.js

import React, { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import useAccounts from '../hooks/useAccounts';

const eur = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

function Accounts() {
  const { accounts, lastImport, fetchAccounts, fetchLastImport, createAccount, loading } =
    useAccounts();
  const [formData, setFormData] = useState({
    name: '',
    iban: '',
    currency: 'EUR',
    owner: ''
  });
  const [message, setMessage] = useState('');
  const [variant, setVariant] = useState('info');

  // Charger les comptes et le dernier import au démarrage de la page
  useEffect(() => {
    fetchAccounts();
    fetchLastImport();
  }, [fetchAccounts, fetchLastImport]);

  // Met à jour l'état du formulaire à chaque changement dans un champ
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Gère la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      await createAccount(formData);
      setMessage('Compte créé avec succès !');
      setVariant('success');
      setFormData({ name: '', iban: '', currency: 'EUR', owner: '' });
      fetchAccounts();
    } catch (err) {
      setMessage(err.message || 'Une erreur est survenue.');
      setVariant('danger');
    }
  };

  return (
    <div>
      <h1>Gérer les Comptes Bancaires</h1>

      {/* --- Section Formulaire --- */}
      <div className="mb-5">
        <h2>Ajouter un nouveau compte</h2>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Nom du Compte</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>Propriétaire</Form.Label>
              <Form.Control
                type="text"
                name="owner"
                value={formData.owner}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>IBAN</Form.Label>
              <Form.Control
                type="text"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>Devise</Form.Label>
              <Form.Control
                type="text"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
              />
            </Form.Group>
          </Row>
          <Button variant="primary" type="submit" disabled={loading.createAccount}>
            {loading.createAccount ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              'Créer le compte'
            )}
          </Button>
        </Form>
        {message && (
          <Alert variant={variant} className="mt-3">
            {message}
          </Alert>
        )}
      </div>

      {/* --- Dernier import : lot remplacé au prochain import --- */}
      <div className="mb-5">
        <h2>Dernières transactions importées</h2>
        {loading.lastImport ? (
          <Spinner animation="border" size="sm" />
        ) : !lastImport || !lastImport.transactions.length ? (
          <Alert variant="light" className="border">
            Aucun lot d'import récent. Ce tableau se remplira au prochain import de relevé.
          </Alert>
        ) : (
          <>
            <Alert variant="warning" className="py-2">
              ⚠️ <strong>À vérifier</strong> : ces {lastImport.transactions.length} transactions
              proviennent du dernier import (
              {new Date(lastImport.importedAt).toLocaleString('fr-FR')}). Contrôle leurs catégories
              et leur cohérence — ce tableau sera remplacé au prochain import.
            </Alert>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Compte</th>
                  <th>Description</th>
                  <th>Catégorie</th>
                  <th className="text-end">Montant</th>
                </tr>
              </thead>
              <tbody>
                {lastImport.transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>{new Date(tx.date).toLocaleDateString('fr-FR')}</td>
                    <td className="text-muted">{tx.account?.name}</td>
                    <td>{tx.description}</td>
                    <td>
                      {tx.category ? (
                        <span>
                          <span
                            className="d-inline-block rounded-circle me-1"
                            style={{ width: 9, height: 9, backgroundColor: tx.category.color }}
                          />
                          {tx.category.name}
                        </span>
                      ) : (
                        <span className="text-warning fw-semibold">Non catégorisée</span>
                      )}
                    </td>
                    <td
                      className={`text-end fw-semibold ${Number(tx.amount) >= 0 ? 'text-success' : 'text-danger'}`}
                    >
                      {eur(tx.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </div>

      {/* --- Section Liste des Comptes --- */}
      <h2>Comptes existants</h2>
      {loading.accounts ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Propriétaire</th>
              <th>IBAN</th>
              <th>Devise</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.id}>
                <td>{account.name}</td>
                <td>{account.owner}</td>
                <td>{account.iban}</td>
                <td>{account.currency}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default Accounts;
